import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon,
  ZapIcon,
  WindIcon,
  MoonIcon,
  ArrowLeftIcon,
  AlertCircleIcon,
  ActivityIcon,
  LightbulbIcon,
  ChevronRightIcon,
  DropletsIcon,
  BedDoubleIcon,
  XIcon,
  SunriseIcon,
  SunsetIcon,
  PersonStandingIcon,
} from 'lucide-react';
import { SleepCareDemo } from './SleepCareDemo';
import { useZonesTelemetry } from '../hooks/useZonesTelemetry';
import { useTranslation } from 'react-i18next';
import { getComfortState, scoreHumidity, scoreTemp, scoreToColor } from '../utils/comfort';
import { thingsboardService } from '../services/thingsboard';
import {
  type SensorType,
  getSensorIcon,
  getSensorColorObject,
  getSensorTypeName,
  formatErvStatus,
} from '../utils/sensorUtils';
import {
  type AirQualityData,
  scoreCO2,
  scoreTVOC,
  scorePM25,
  getAQIGrade,
  overallAqiScore,
} from '../utils/aqiUtils';
import { getEnergyGrade } from '../utils/energyUtils';

interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  roomId: string;
  active: boolean;
  x: number;
  y: number;
}
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: any;
}
interface ZoneDetailSidebarProps {
  room: Room;
  onClose: () => void;
  sensors?: Sensor[];
  onSelectSensor?: (sensor: Sensor) => void;
}


// Sleep score circular gauge SVG
function SleepScoreGauge({ score }: {score: number;}) {
  const radius = 110;
  const strokeWidth = 14;
  const circumference = 2 * Math.PI * radius;
  const progress = score / 100 * circumference;
  const gap = circumference - progress;
  return (
    <div className="relative flex items-center justify-center">
      <svg width="260" height="260" viewBox="0 0 260 260">
        {/* Background circle */}
        <circle
          cx="130"
          cy="130"
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={strokeWidth} />

        {/* Progress arc */}
        <motion.circle
          cx="130"
          cy="130"
          r={radius}
          fill="none"
          stroke="url(#sleepGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${gap}`}
          strokeDashoffset={circumference * 0.25}
          initial={{
            strokeDasharray: `0 ${circumference}`
          }}
          animate={{
            strokeDasharray: `${progress} ${gap}`
          }}
          transition={{
            duration: 1.2,
            ease: 'easeOut',
            delay: 0.3
          }} />

        <defs>
          <linearGradient
            id="sleepGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%">

            <stop offset="0%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-4xl font-bold text-white font-roboto"
          initial={{
            opacity: 0,
            scale: 0.5
          }}
          animate={{
            opacity: 1,
            scale: 1
          }}
          transition={{
            delay: 0.5,
            type: 'spring',
            stiffness: 200
          }}>

          {score}
        </motion.span>
        <span className="text-[0.75rem] text-slate-400 uppercase tracking-wider font-bold">
          Score
        </span>
      </div>
    </div>);

}
// Sleep stage bar
function SleepStageBar({
  label,
  percent,
  duration,
  color,
  delay
}: {label: string;percent: number;duration: string;color: string;delay: number;}) {
  return (
    <motion.div
      className="flex items-center gap-3"
      initial={{
        opacity: 0,
        x: 20
      }}
      animate={{
        opacity: 1,
        x: 0
      }}
      transition={{
        delay
      }}>

      <span
        className="text-[0.85rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md min-w-[5.2rem] text-center"
        style={{
          backgroundColor: color,
          color: '#fff'
        }}>

        {label}
      </span>
      <div className="flex-1 h-3 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{
            backgroundColor: color
          }}
          initial={{
            width: 0
          }}
          animate={{
            width: `${percent}%`
          }}
          transition={{
            duration: 0.8,
            delay: delay + 0.2,
            ease: 'easeOut'
          }} />

      </div>
      <span className="text-[0.9rem] text-slate-400 font-roboto min-w-[7rem] text-right">
        {percent}% {duration}
      </span>
    </motion.div>);

}



// ─── AQI Triangle SVG ───
// Reusable mini bar chart for score visualization
function MiniBarChart({
  bars,
  maxHeight = 70
}: {bars: {label: string;value: number;maxValue: number;color: {r: number;g: number;b: number;};}[];maxHeight?: number;}) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="flex items-end"
        style={{
          gap: 1
        }}>
        
        {bars.map((b) => {
          const barH = Math.max(
            3,
            b.value / Math.max(b.maxValue, 1) * maxHeight
          );
          return (
            <div
              key={b.label}
              className="flex flex-col items-center"
              style={{
                width: 60
              }}>
              
              <div
                style={{
                  width: 50,
                  height: barH,
                  borderRadius: 3,
                  backgroundColor: `rgba(${b.color.r},${b.color.g},${b.color.b},0.6)`,
                  border: `3px solid rgba(${b.color.r},${b.color.g},${b.color.b},0.8)`,
                  transition: 'height 0.4s ease'
                }} />
              
            </div>);

        })}
      </div>
      {/* X-axis line */}
      <div
        style={{
          width: bars.length * 50 + (bars.length - 1) * 10,
          height: 3
        }}
        className="bg-white/40" />
      
      {/* X-axis labels */}
      <div
        className="flex"
        style={{
          gap: 1,
          marginTop: 2
        }}>
        
        {bars.map((b) =>
        <span
          key={b.label}
          className="font-roboto text-white/60 leading-none text-center"
          style={{
            fontSize: '0.5rem',
            width: 60
          }}>
          
            {b.label}
          </span>
        )}
      </div>
    </div>);

}
function AQITriangle({ airData }: { airData: AirQualityData; }) {
  const co2Score = scoreCO2(airData.co2);
  const tvocScore = scoreTVOC(airData.tvoc);
  const pm25Score = scorePM25(airData.pm25);
  const overallScore = Math.round(overallAqiScore(airData));
  const grade = getAQIGrade(overallScore);
  const bars = [
  {
    label: 'PM2.5',
    value: airData.pm25,
    maxValue: 150,
    color: scoreToColor(pm25Score)
  },
  {
    label: 'CO₂',
    value: airData.co2,
    maxValue: 800,
    color: scoreToColor(co2Score)
  },
  {
    label: 'TVOC',
    value: airData.tvoc,
    maxValue: 200,
    color: scoreToColor(tvocScore)
  }];

  return (
    <div
      className="flex items-center"
      style={{
        gap: 10
      }}>
      
      {/* Overall AQI Score */}
      <div className="flex flex-col items-center">
        <span
          className={`font-roboto font-extrabold ${grade.color}`}
          style={{
            fontSize: '1rem',
            lineHeight: 1,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))'
          }}>
          
          {overallScore}
        </span>
      </div>
      <MiniBarChart bars={bars} />
    </div>);

}
export function ZoneDetailSidebar({
  room,
  onClose,
  sensors = [],
  onSelectSensor
}: ZoneDetailSidebarProps) {
  const { t } = useTranslation();
  const [showSleepCare, setShowSleepCare] = useState(false);
  const [showSleepDemo, setShowSleepDemo] = useState(false);
  const isBedroomRoom = sensors.some(sensor => sensor.type === 'RADAR');
  const { zonesData } = useZonesTelemetry(
    [room.id],
    ['temperature', 'calculated_24h_power', 'humidity', 'CO2', 'TVOCs', 'dust', 'presence_state', 'light_state']
  );
  
  const sensorIds = useMemo(() => sensors.map(s => s.id).filter(Boolean), [sensors]);
  const { zonesData: sensorTelemetry } = useZonesTelemetry(sensorIds, ['erv', 'ERV'], 'DEVICE');
  
  const roomData = zonesData[room.id] || {};
  const temp = roomData['temperature']?.[0]?.value ? Number(roomData['temperature'][0].value) : room.temp;
  const humidity = roomData['humidity']?.[0]?.value ? Number(roomData['humidity'][0].value) : 50;
  const power = roomData['calculated_24h_power']?.[0]?.value ? Number(roomData['calculated_24h_power'][0].value) : 0;
  const isOccupied = roomData['presence_state']?.[0]?.value ? Number(roomData['presence_state'][0].value) : 0;
  const isLightOn = roomData['light_state']?.[0]?.value ? Number(roomData['light_state'][0].value) : 0;

  const airData: AirQualityData = {
    co2: roomData['CO2']?.[0]?.value ? Number(roomData['CO2'][0].value) : 0,
    tvoc: roomData['TVOCs']?.[0]?.value ? Number(roomData['TVOCs'][0].value) : 0,
    pm25: roomData['dust']?.[0]?.value ? Number(roomData['dust'][0].value) : 0
  };

  const overallScore = Math.round(overallAqiScore(airData));
  const grade = getAQIGrade(overallScore);
  const comfortState = getComfortState(temp, humidity);
  const tempScore = scoreTemp(temp);
  const humidityScore = scoreHumidity(humidity);
  const tCol = scoreToColor(tempScore);
  const hCol = scoreToColor(humidityScore);

  const alerts: string[] = [];
  if (temp > 28) alerts.push(t('zone.alert_temp_high'));
  if (airData.co2 > 1000) alerts.push(t('zone.alert_co2_high'));
  if (power > 5) alerts.push(t('zone.alert_power_high'));
  const hasAlerts = alerts.length > 0;
  // Mock sleep data
  const sleepData = {
    score: 78,
    stages: [
    {
      label: 'AWAKE',
      percent: 26,
      duration: '02h 15m',
      color: '#a78bfa'
    },
    {
      label: 'REM',
      percent: 23,
      duration: '01h',
      color: '#7c3aed'
    },
    {
      label: 'NREM',
      percent: 50,
      duration: '04h 16m',
      color: '#4f46e5'
    }],

    times: [
    {
      label: 'Bed in',
      time: '16:25',
      icon: 'bedin'
    },
    {
      label: 'Bed out',
      time: '03:16',
      icon: 'bedout'
    },
    {
      label: 'Wake up',
      time: '03:16',
      icon: 'wakeup'
    },
    {
      label: 'Sleep',
      time: '18:52',
      icon: 'sleep'
    }]

  };
  
  const energyState = getEnergyGrade(power);

  return (
    <div className="w-full flex flex-col gap-3 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-y-auto">
      {/* Header with Back Button */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white">

          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <room.icon className="w-6 h-6 text-white/80" />
          <h1 className="text-xl font-bold text-white tracking-tight mb-0.5">
            {room.name}
          </h1>
        </div>
        
        {isBedroomRoom &&
        <motion.button
          initial={{
            opacity: 0,
            scale: 0.8
          }}
          animate={{
            opacity: 1,
            scale: 1
          }}
          transition={{
            delay: 0.2
          }}
          onClick={() => setShowSleepDemo(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/30 hover:text-indigo-200 transition-all active:scale-95 mb-1">

            <BedDoubleIcon className="w-4 h-4" />
            <span className="text-[0.75rem] font-bold">{t('zone.sleep_care')}</span>
        </motion.button>
        }

    
      <div className="flex items-center gap-3 ml-auto">
          {/* Light Toggle Switch */}
          <button
            onClick={async () => {
              try {
                // Determine target state (if currently on, turn off, vice versa)
                const targetState = !isLightOn;
                await thingsboardService.sendOneWayRpc(room.id, 'set_switch', targetState);
                console.log(`Sent RPC to ${room.id}: set_switch = ${targetState}`);
              } catch (error) {
                console.error('Failed to send RPC command for light switch', error);
              }
            }}
            className={`relative w-16 h-8 rounded-full transition-colors duration-300 focus:outline-none flex-shrink-0 ${isLightOn ? 'bg-amber-500' : 'bg-slate-700'}`}>
            
            <div
              className={`absolute top-1 left-1.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-300 ${isLightOn ? 'translate-x-7' : 'translate-x-0'}`} />
            
          </button>
          
          {/* Light + Presence icons */}
          <div
            className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isLightOn ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-slate-600'}`}
            title={t('zone.light_status')}>
            
            <LightbulbIcon
              className="w-7 h-7"
              fill={isLightOn ? 'currentColor' : 'none'} />
            
          </div>
          <div
            className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isOccupied ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-slate-600'}`}
            title={t('zone.presence_status')}>
            
            <PersonStandingIcon
              className="w-7 h-7"
              fill={isOccupied ? 'currentColor' : 'none'} />
            
          </div>
        </div>
      </div>

      {/* Status Cards */}
            <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        className="space-y-3"
      >
      {/* 전기사용량 */}
      <div className="flex items-center justify-between p-3 rounded-xl glass-panel">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${energyState.bgColor}`}>
                <ZapIcon className={`w-4 h-4 ${energyState.color}`} />
              </div>
              <div>
                <div className="text-[0.9rem] font-roboto text-slate-500 uppercase tracking-wider">
                  {t('zone.energy_usage')}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-roboto font-bold ${energyState.color}`}>
                    {t(energyState.label)}
                  </span>
                  <span className={`text-[0.65rem] font-bold px-1.5 py-0.5 rounded ${energyState.bgColor} ${energyState.color}`}>
                    {t(energyState.description)}
                  </span>
                </div>
              </div>
            </div>
          {/* Energy usage bar chart */}
          <div className="flex flex-col items-end">
            <div className={`text-[1rem] font-roboto font-bold ${energyState.color}`}>
              {(power/1000).toLocaleString(undefined,{ minimumFractionDigits: 1, maximumFractionDigits: 1 })} <span className='text-[0.7rem] text-slate-500'> kWh</span>
            </div>
            <div className="text-[0.6rem] text-slate-400 mt-0.5">
              최근 24시간
            </div>
          </div>
          
      </div> 

      {/* 쾌적성 */}
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5">
          <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400">
                <ThermometerIcon className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[0.9rem] font-roboto text-slate-500 uppercase tracking-wider">
                  {t('zone.comfortability')}
                </div>
                <div className={`text-sm font-medium ${comfortState.text} flex items-center gap-1`}>
                  {comfortState.icon === 'droplet' && <DropletsIcon className="w-3.5 h-3.5 text-blue-400 fill-blue-400" />}
                  {t(comfortState.label)}
                </div>
              </div>
            </div>
          {/* Temperature & Humidity bar chart */}
          <div className="text-[1rem] font-roboto font-bold text-right">
            <span style={{ color: `rgb(${tCol.r}, ${tCol.g}, ${tCol.b})` }}>{temp.toFixed(1)}</span><span className='text-[0.7rem] text-slate-500'> °C</span>   <span style={{ color: `rgb(${hCol.r}, ${hCol.g}, ${hCol.b})` }}>{humidity.toFixed(0)}</span><span className='text-[0.7rem] text-slate-500'> %</span>
          </div>
          </div>
        </div>

        {/* 종합 공기질 - same level, with triangle on right */}
        <div className="flex items-stretch p-3 rounded-xl glass-panel gap-3">
          {/* Left: icon + label + score */}
          <div className="flex flex-col min-w-0 items-start justify-start">
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 rounded-lg ${grade.bgColor}`}>
                <WindIcon className={`w-4 h-4 ${grade.color}`} />
              </div>
              <div>
                <div className="text-[0.9rem] font-roboto text-slate-500 uppercase tracking-wider">
                  {t('zone.overall_aqi')}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm font-roboto font-bold ${grade.color}`}>
                    
                    {t(grade.label)}
                  </span>
                  <span
                    className={`text-[0.65rem] font-bold px-1.5 py-0.5 rounded ${grade.bgColor} ${grade.color}`}>
                    
                    {t(grade.description)}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-[0.6rem] text-slate-500 ml-11"></div>
            </div>

          {/* Right: triangle visualization */}
          <div className="flex-shrink-0 flex items-center ml-auto">
            <AQITriangle airData={airData} />
          </div>
        </div>
</motion.div>

      {/* Sensor List */}
      {sensors.length > 0 &&
      <motion.div
        initial={{
          opacity: 0,
          y: 20
        }}
        animate={{
          opacity: 1,
          y: 0
        }}
        transition={{
          delay: 0.15
        }}
        className="glass-panel rounded-2xl p-4">

          <div className="flex items-center gap-2 mb-3 text-slate-400">
            <ActivityIcon className="w-4 h-4 text-cyan-400" />
            <span className="text-[0.9rem] font-roboto text-slate-400 uppercase tracking-wider">
              {t('zone.zone_sensors', { count: sensors.length })}
            </span>
          </div>
          <div className="space-y-2">
            {sensors.map((sensor, i) => {
            const Icon = getSensorIcon(sensor.type);
            const colors = getSensorColorObject(sensor.type);

            return (
              <motion.button
                key={sensor.id}
                initial={{
                  opacity: 0,
                  x: -10
                }}
                animate={{
                  opacity: 1,
                  x: 0
                }}
                transition={{
                  delay: 0.2 + i * 0.05
                }}
                onClick={() => onSelectSensor?.(sensor)}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.07] hover:border-white/10 transition-all group cursor-pointer">

                  {/* Sensor Icon */}
                  <div
                  className={`p-2 rounded-lg ${colors.bg} ${colors.icon} flex-shrink-0`}>

                    <Icon className="w-4 h-4" />
                  </div>

                  {/* Sensor Info */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium text-slate-200 truncate">
                      {sensor.label}
                    </div>
                    <div className="text-[0.7rem] text-slate-500 font-roboto">
                      {getSensorTypeName(sensor.type)}
                    </div>
                  </div>

                  {/* Status Indicator */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1.5">
                      <div className="relative">
                        <div
                        className={`w-2 h-2 rounded-full ${sensor.active ? colors.dot : 'bg-slate-600'}`} />

                        {sensor.active &&
                      <div
                        className={`absolute inset-0 w-2 h-2 rounded-full ${colors.dot} animate-ping opacity-75`}
                        style={{
                          animationDuration: '2s'
                        }} />

                      }
                      </div>
                      <span
                      className={`text-[0.65rem] font-roboto font-bold ${sensor.active ? 'text-emerald-400' : 'text-slate-600'}`}>

                        {sensor.active ? 'ON' : 'OFF'}
                      </span>
                    </div>

                    <ChevronRightIcon className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 transition-colors" />
                  </div>
                </motion.button>);

          })}
          </div>
        </motion.div>
      }

      {/* Sleep Care Popup Modal */}
      <AnimatePresence>
        {showSleepCare &&
        <motion.div
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
          exit={{
            opacity: 0
          }}
          transition={{
            duration: 0.2
          }}
          className="fixed inset-0 z-[200] flex items-center justify-center"
          onClick={() => setShowSleepCare(false)}>

            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" />

            {/* Modal Card */}
            <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
              y: 20
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }}
            exit={{
              opacity: 0,
              scale: 0.9,
              y: 20
            }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 w-[94%] max-w-xl mx-auto">

              <div className="glass-panel bg-[#0a0f1a]/95 rounded-2xl overflow-hidden border border-indigo-500/20">
                {/* Header */}
                <div className="flex items-center justify-between px-7 py-5 border-b border-white/5 bg-gradient-to-r from-indigo-600/20 to-violet-600/20">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-indigo-500/30">
                      <MoonIcon className="w-5 h-5 text-indigo-300" />
                    </div>
                    <div>
                      <div className="text-base font-bold text-white">
                        {t('zone.sleep_care_report')}
                      </div>
                      <div className="text-[0.8rem] text-indigo-300/70 font-roboto">
                        {room.name} · {t('zone.today')}
                      </div>
                    </div>
                  </div>
                  <button
                  onClick={() => setShowSleepCare(false)}
                  className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors">

                    <XIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-7">
                  {/* Score + Stages Row */}
                  <div className="flex items-center gap-8">
                    {/* Circular Score */}
                    <div className="flex-shrink-0">
                      <SleepScoreGauge score={sleepData.score} />
                    </div>

                    {/* Sleep Stages */}
                    <div className="flex-1 flex flex-col gap-4">
                      {sleepData.stages.map((stage, i) =>
                    <SleepStageBar
                      key={stage.label}
                      label={stage.label}
                      percent={stage.percent}
                      duration={stage.duration}
                      color={stage.color}
                      delay={0.3 + i * 0.1} />

                    )}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-white/10 my-7" />

                  {/* Time Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    {sleepData.times.map((item, i) => {
                    const icons: Record<string, React.ReactNode> = {
                      bedin: <MoonIcon className="w-5 h-5" />,
                      bedout: <SunriseIcon className="w-5 h-5" />,
                      wakeup: <SunsetIcon className="w-5 h-5" />,
                      sleep: <MoonIcon className="w-5 h-5" />
                    };
                    return (
                      <motion.div
                        key={item.label}
                        initial={{
                          opacity: 0,
                          y: 10
                        }}
                        animate={{
                          opacity: 1,
                          y: 0
                        }}
                        transition={{
                          delay: 0.6 + i * 0.08
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/[0.03] border border-white/5">

                          <div className="text-indigo-400/60">
                            {icons[item.icon]}
                          </div>
                          <span className="text-xl font-roboto font-bold text-white">
                            {item.time}
                          </span>
                          <span className="text-[0.75rem] text-slate-500 uppercase tracking-wider font-bold">
                            {item.label}
                          </span>
                        </motion.div>);

                  })}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>

      {/* Sleep Care Demo Popup */}
      <SleepCareDemo
        isOpen={showSleepDemo}
        sensors={sensors}
        onClose={() => setShowSleepDemo(false)}
        onOpenReport={() => setShowSleepCare(true)} />
      
    </div>);

}